import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useResponsive } from '@actual-app/components/hooks/useResponsive';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { Page } from '#components/Page';
import { useCategories } from '#hooks/useCategories';
import { useLocale } from '#hooks/useLocale';
import { useSyncedPref } from '#hooks/useSyncedPref';

import { cor, tela, texto } from './estilo';
import { Cabecalho } from './inicio/Cabecalho';
import { GastoMedioPorTipo } from './inicio/GastoMedioPorTipo';
import { Heroi } from './inicio/Heroi';
import { RendaDosMeses } from './inicio/RendaDosMeses';
import { ResumoCompromissos } from './inicio/ResumoCompromissos';
import { TresColunas } from './inicio/TresColunas';
import { montarContasFixas, totalPrevisto } from './motor/contasFixas';
import type { Escopo } from './motor/escopo';
import { montarSerie } from './motor/historicoMensal';
import { calcularMedias } from './motor/medias';
import { CHAVE_PREF, lerMapa } from './motor/tiposDeGasto';
import { useAgendadosDoMes } from './motor/useAgendadosDoMes';
import { useHistorico } from './motor/useHistorico';
import { useMesFinanceiro } from './motor/useMesFinanceiro';

export function Inicio() {
  const { t } = useTranslation();
  const locale = useLocale();
  const { isNarrowWidth } = useResponsive();
  const [mes, setMes] = useState(monthUtils.currentMonth());
  const [escopo, setEscopo] = useState<Escopo>('pessoal');

  const mesAnterior = monthUtils.prevMonth(mes);
  const doMes = useMesFinanceiro(mes, escopo);
  const doMesAnterior = useMesFinanceiro(mesAnterior, escopo);

  /**
   * REGRA 5 (spec §8.4): mês (quase) vazio abre no anterior, com aviso — em
   * vez de mostrar uma tela vazia que parece defeito. É o caso central do
   * projeto: o app nasce vazio (zero despesa/receita lançada — ver
   * CLAUDE.md) e a tela pode abrir bem no início de um mês novo, antes de
   * qualquer lançamento.
   *
   * ⚠️ A régua LITERAL do spec é "menos de 3 lançamentos" — não dá pra medir
   * a CONTAGEM exata com o que `useMesFinanceiro` (Task 7) devolve: só
   * `ultimoDia` (a data do lançamento mais recente), nunca quantos são.
   * Aproximamos por `ultimoDia === null` (ZERO lançamentos no mês). Isso
   * cobre o caso dominante (mês sem nenhum lançamento) mas não pega um mês
   * com 1 ou 2 lançamentos soltos — medir "< 3" de verdade exigiria
   * acrescentar uma contagem ao retorno de `useMesFinanceiro`, fora da
   * lista de arquivos desta tarefa (Task 8). Ver o relatório da Task 8.
   *
   * ⚠️ Calculado de forma DERIVADA a cada render (nunca por `useEffect` +
   * `setState`), de propósito, por dois motivos: (1) `doMesAnterior` já é
   * buscado de qualquer forma, pra alimentar a comparação do Herói —
   * reaproveitar o mesmo resultado evita uma 3ª consulta; (2) um
   * redirecionamento via efeito só rodaria DEPOIS do primeiro render, que
   * já teria mostrado — ainda que por um instante — o herói zerado do mês
   * vazio (`resumirMes` devolve tudo zerado, não `null`, pra um mês sem
   * lançamento). Derivar evita esse flash.
   *
   * ⚠️⚠️ `doMes.erro === null` é OBRIGATÓRIO aqui (Fix round 1 da Task 8,
   * Finding 1 crítico). `useMesFinanceiro`, ao falhar, faz `setResumo(null)`
   * e `setErro(e)` mas NÃO mexe em `ultimoDia` — que fica `null` desde a
   * carga inicial. Sem esse guard, uma consulta do mês atual que QUEBROU
   * (banco fora do ar, por exemplo) é lida como "mês vazio" e a tela troca
   * em silêncio pro mês anterior, mostrando um número REAL e crível — só
   * que do mês ERRADO — sem nenhum sinal de que algo falhou. Isso é pior
   * que o zero que `useMesFinanceiro` documenta evitar: zero pelo menos
   * parece estranho; um número plausível do vizinho, não. Com o guard, uma
   * falha de leitura nunca vira REGRA 5 — ela cai direto no bloco de erro
   * abaixo, que mostra `dadosExibidos.erro` (aqui, `doMes.erro`).
   */
  const mesQuaseVazio =
    !doMes.carregando && doMes.erro === null && doMes.ultimoDia === null;
  const mesExibido = mesQuaseVazio ? mesAnterior : mes;
  const dadosExibidos = mesQuaseVazio ? doMesAnterior : doMes;

  /**
   * Só compara com o mês anterior quando ele tem lançamento de verdade —
   * comparar contra um mês sem nenhum dado (ex.: o primeiro mês de uso do
   * app) fabricaria uma "alta"/"queda" que não existe.
   *
   * ⚠️ Quando `mesQuaseVazio` é true, o mês EXIBIDO já é `mesAnterior`, e o
   * "mês anterior a ELE" seria dois meses antes de `mes` — dado que não é
   * buscado aqui, pra não somar uma 3ª consulta só pra um estado que já é
   * de contingência. Nesse caso a comparação fica de fora — ver o
   * relatório da Task 8.
   */
  const livreMesAnterior =
    !mesQuaseVazio && doMesAnterior.resumo && doMesAnterior.ultimoDia !== null
      ? doMesAnterior.resumo.livre
      : null;

  /**
   * Task 10 — "Contas do mês": planejado × realizado de cada conta fixa,
   * cruzando os AGENDADOS (schedules nativos) com as `linhas` do mês
   * EXIBIDO (`dadosExibidos`, não `mes` — REGRA 5 pode estar mostrando o
   * mês anterior, e as contas fixas exibidas têm que ser as DELE).
   *
   * ⚠️⚠️ OBRIGAÇÃO HERDADA DA TASK 9 (ver os JSDocs de `inicio/Heroi.tsx`,
   * `inicio/TresColunas.tsx` e `inicio/ContasDoMes.tsx`): o Herói (Task 8)
   * já deveria descontar as fixas AINDA NÃO PAGAS do "Livre pra decidir",
   * mas até esta tarefa `useMesFinanceiro` sempre calculava
   * `extras.fixasPrevistas` como zero (nenhum chamador tinha essa
   * informação) — então o herói e as três colunas concordavam por
   * coincidência (os dois ignoravam fixas pendentes). Agora que
   * `totalPrevisto(contas)` é conhecido, ele é a peça que faltava: em vez
   * de mudar a ASSINATURA de `useMesFinanceiro` (o que arriscaria
   * recalcular/re-consultar `linhas` a cada mudança de `fixasPrevistas` —
   * ver o relatório da Task 10 pra essa análise), o AJUSTE é feito aqui,
   * na borda de renderização: `resumoExibido` é o MESMO `resumo` que
   * `useMesFinanceiro` calculou, só com `fixasPrevistas`/`livre`
   * corrigidos. `TresColunas` não muda (ela nunca lê `fixasPrevistas` nem
   * `livre` — só `entrou`/`gastosVariaveis`/`fixasRealizadas`/`aportes`/
   * `naoEhGasto`, todos inalterados), e é exatamente esse "Gastei" que NÃO
   * inclui as fixas pendentes — o que faz a soma das três colunas dar UM
   * VALOR MAIOR que o herói ajustado. `ResumoCompromissos` é o bloco que
   * deixa essa diferença visível e explicada (ver
   * `data-testid="resumo-compromissos-aviso"`, em
   * `inicio/ResumoCompromissos.tsx`) — não mais `ContasDoMes` (Revisão final
   * da branch, Finding 2: este JSDoc apontava para um componente que a tela
   * não renderiza mais desde a Task 9; `ContasDoMes` segue existindo, só que
   * sem chamador vivo aqui — ver o cabeçalho dela).
   *
   * ⚠️ Fonte do `valorPrevisto` de cada conta fixa: ver o JSDoc de
   * `motor/useAgendadosDoMes.ts` — é uma DIVERGÊNCIA DELIBERADA do texto
   * do brief ("orçamento nativo em modo tracking"), aceita no fix round 1
   * e documentada lá e no relatório da tarefa.
   *
   * ⚠️⚠️ FIX ROUND 1 (Finding 1, Critical) — `escopo` (o mesmo estado do
   * `Cabecalho`, já usado em `useMesFinanceiro`) É PASSADO tanto pra
   * `useAgendadosDoMes` (que exclui um agendado do escopo errado — ver o
   * JSDoc de lá pra como isso é decidido sem a tabela `schedules` ter
   * campo de nota) quanto pra `montarContasFixas` (que filtra as PRÓPRIAS
   * `linhas` usadas pra achar o realizado). Sem isso, uma conta fixa
   * `#empresa` aparecia em "Contas do mês" com o dono no escopo pessoal —
   * e se não paga, era subtraída do `livre` PESSOAL por engano.
   *
   * ⚠️⚠️ FIX ROUND 1 (Critical, Task 9) — `carregando`/`erro` de
   * `useAgendadosDoMes` SÃO desestruturados aqui, de novo (uma versão
   * anterior desta tarefa tinha tirado os dois, porque só `ContasDoMes.tsx`
   * os lia e ela saiu da montagem — ERRADO: revertido no fix round 1).
   * `fixasPrevistas = totalPrevisto(contasDoMes)` tem DOIS significados
   * possíveis quando dá zero: "nenhuma conta fixa a pagar" (bom) OU "a
   * consulta de agendados falhou, `agendados` virou `[]` por default" (uma
   * mentira). Só `agendadosErro` distingue os dois — e `dadosExibidos.erro`
   * NÃO ajuda aqui: a consulta de `schedules` é INDEPENDENTE da de
   * transações (ver o JSDoc de `useAgendadosDoMes.ts`: best-effort, não
   * derruba o resumo do mês), então o mês pode carregar limpo enquanto só
   * os agendados falharam, sem nenhum banner de erro existente pra cobrir.
   *
   * Sem esse sinal em algum lugar, DOIS números mentem para o lado
   * OTIMISTA ao mesmo tempo, sem nenhum aviso: `ResumoCompromissos`
   * mostraria "R$0,00 de R$0,00" (como se não houvesse compromisso
   * nenhum) e — pior — `resumoExibido.livre` abaixo, o NÚMERO PRINCIPAL da
   * tela (o Herói), ficaria alto demais, porque `fixasPrevistas` (que ele
   * desconta) também é zero por engano. É exatamente a classe de bug que
   * `ContasDoMes.tsx` (Fix round 1, Finding 3) foi corrigido para evitar —
   * a tela perdeu essa proteção ao trocar de bloco, até este fix round.
   *
   * A correção tem DUAS pontas, cada uma cobrindo metade do risco:
   * `agendadosErro`/`agendadosCarregando` descem para `ResumoCompromissos`
   * (que troca os números por uma mensagem — ver o JSDoc de
   * `inicio/ResumoCompromissos.tsx`), e o AVISO abaixo do Herói
   * (`data-testid="heroi-fixas-incertas"`) avisa especificamente que o
   * "Livre pra decidir" pode estar otimista demais — porque `TresColunas`
   * nunca vê esse problema (ela não lê `fixasPrevistas`), só o Herói.
   */
  const {
    agendados,
    carregando: agendadosCarregando,
    erro: agendadosErro,
  } = useAgendadosDoMes(mesExibido, dadosExibidos.linhas, escopo);
  const contasDoMes = montarContasFixas(
    agendados,
    dadosExibidos.linhas,
    escopo,
  );
  const fixasPrevistas = totalPrevisto(contasDoMes);
  const resumoExibido = dadosExibidos.resumo
    ? {
        ...dadosExibidos.resumo,
        fixasPrevistas,
        livre: dadosExibidos.resumo.livre - fixasPrevistas,
      }
    : null;

  /**
   * `nomesDeCategoria`: reusa o hook NATIVO do Actual (`useCategories`, já
   * usado em `ManageRules.tsx`/`CategoryAutocomplete.tsx` etc.) em vez de
   * escrever uma query própria — os motores abaixo (Task 9) só precisam do
   * id→nome, e o Actual já mantém essa lista carregada e em cache
   * (react-query) pro resto do app; escrever uma segunda consulta seria
   * duplicar o que já existe.
   *
   * Memoizado em `categorias?.list` — sem isso, um `new Map(...)` novo a
   * cada render faria `calcularMedias` (que só lê o mapa, não se importa com
   * IDENTIDADE dele) recalcular à toa, mas principalmente evita recriar
   * objeto sempre que `Inicio` re-renderiza por qualquer outro motivo (ex.:
   * o polling de `useMesFinanceiro`).
   *
   * ⚠️⚠️ Declarado ANTES do bloco de médias logo abaixo, de propósito:
   * `calcularMedias` (Task 9) precisa do mapa pronto. Na versão anterior
   * desta tela (Task 11), `nomesDeCategoria` só existia LOGO ANTES do
   * `return` — tarde demais para o que vem agora.
   */
  const { data: categorias } = useCategories();
  const nomesDeCategoria = useMemo(
    () => new Map(categorias?.list.map(c => [c.id, c.name]) ?? []),
    [categorias],
  );

  /**
   * ⭐ Task 9 (painel-panorama) — o panorama da tela Mês enxuta: quanto já
   * foi pago em fixas, o histórico de meses (pro gráfico de renda) e o
   * gasto médio por tipo (essencial/luxo/investimento), contra este mês.
   *
   * `pagoEmFixas` soma o `realizado` de cada conta de `contasDoMes` (`null`
   * = ainda não paga, entra como zero) — é o "pago" que `ResumoCompromissos`
   * mostra ao lado de `fixasPrevistas` ("a pagar"), sem duplicar o cálculo
   * que `totalPrevisto` já faz para o lado "não pago".
   *
   * ⚠️ Janela FIXA de 6 meses aqui (a tela Médias é que tem seletor): a tela
   * Mês é para bater o olho, e um seletor a mais seria decisão a tomar numa
   * tela que existe justamente para não exigir decisão nenhuma.
   *
   * `mesExibido` alimenta `useHistorico` (não `mes`) pela MESMA razão de
   * `contasDoMes`/`nomesDeCategoria` acima: a REGRA 5 pode estar mostrando
   * o mês anterior, e a série/médias exibidas têm que ser as DELE.
   *
   * Duas chamadas de `calcularMedias`: uma para a janela inteira (o que
   * alimenta a MÉDIA de cada tipo) e outra só para `mesExibido` (o que
   * alimenta o valor DESTE mês) — a mesma técnica que `Medias.tsx` já usa
   * para sua coluna "este mês", aqui aplicada ao balde por TIPO em vez de
   * por categoria.
   */
  const pagoEmFixas = contasDoMes.reduce(
    (soma, c) => soma + (c.realizado ?? 0),
    0,
  );

  /**
   * ⚠️⚠️ Revisão final da branch (Finding 1, CRITICAL) — `historico.erro` e
   * `historico.carregando` eram calculados e NUNCA lidos: `GastoMedioPorTipo`
   * e `RendaDosMeses` só recebiam `.linhas`/`.meses`. Quando a consulta de
   * `useHistorico` falha, `linhas` vem `[]` e a JANELA de meses continua
   * vindo de `.meses` (não das linhas) — então `montarSerie` devolvia 6
   * meses ZERADOS (`RendaDosMeses` desenhava 6 barras de 1px, que o JSDoc
   * dela mesma define como "foi zero") e `calcularMedias` devolvia
   * `mesesConsiderados: 0` com todos os baldes em R$ 0,00, dizendo "média de
   * 0 meses com movimento" — um número plausível e ERRADO, sem nenhum
   * aviso. É a MESMA classe de bug que a Task 9 corrigiu para os agendados
   * (`agendadosErro`, algumas linhas acima) — só que no bloco vizinho.
   *
   * `historicoOk` é a mesma guarda que `Medias.tsx` já usa por EARLY RETURN
   * (Fix round 1 de lá): aqui não dá pra fazer early return (a tela mostra
   * vários blocos independentes no mesmo `return`), então a guarda decide
   * se `montarSerie`/`calcularMedias` rodam ou não — rodar os dois contra
   * dado que não vai ser mostrado seria trabalho jogado fora a cada render.
   *
   * ⚠️⚠️ REGRA ESCOLHIDA (a mesma nos três blocos que dependem de uma
   * consulta própria: `ResumoCompromissos`, `GastoMedioPorTipo`,
   * `RendaDosMeses`): erro vence tudo, depois carregando, só então os
   * números — a ORDEM que `ResumoCompromissos` já usava (Fix round 1 da
   * Task 9). Escolhida por CONSISTÊNCIA: antes desta correção, navegar de
   * mês fazia `ResumoCompromissos` mostrar "Carregando…" enquanto os outros
   * dois mostrariam (silenciosamente) o dado do mês anterior por um
   * instante — três comportamentos diferentes lado a lado, no mesmo
   * clique. O custo aceito (já documentado como "parked" no relatório da
   * Task 9 só para `ResumoCompromissos`, agora também para os outros dois):
   * um flicker de "Carregando…" a cada troca de mês, mesmo quando já existe
   * dado válido (só) um pouco velho — UX menor, não número errado.
   */
  const historico = useHistorico(mesExibido, 6);
  const historicoOk = !historico.carregando && historico.erro === null;

  const serie = historicoOk
    ? montarSerie(historico.linhas, historico.meses, escopo)
    : [];

  const [prefTipos] = useSyncedPref(CHAVE_PREF);
  const mapaDeTipos = lerMapa(prefTipos);

  const mediasVazias = { mesesConsiderados: 0, porCategoria: [], porTipo: [] };
  const mediasDoPeriodo = historicoOk
    ? calcularMedias(
        historico.linhas,
        historico.meses,
        nomesDeCategoria,
        mapaDeTipos,
        escopo,
      )
    : mediasVazias;
  const mediasDoMes = historicoOk
    ? calcularMedias(
        historico.linhas,
        [mesExibido],
        nomesDeCategoria,
        mapaDeTipos,
        escopo,
      )
    : mediasVazias;
  const gastoDoMesPorTipo = new Map(
    mediasDoMes.porTipo.map(tp => [tp.tipo, tp.media]),
  );

  return (
    <Page header={t('Início')}>
      <View style={tela}>
        {/*
         * ⚠️⚠️ `mes` aqui é o CURSOR de navegação (o mês que o dono pediu
         * pra ver), não `mesExibido` — achado testando no navegador de
         * verdade (não só rodando os testes). Passar `mesExibido` parecia
         * certo (cabeçalho e card sempre concordando sobre qual mês é
         * aquele), mas TRAVAVA a seta ▶: partindo de um mês vazio (ex.:
         * agosto) que caiu pro anterior (julho), `nextMonth(mesExibido) =
         * nextMonth(julho) = agosto` de novo — clicar ▶ virava um no-op
         * (`setMes` com o mesmo valor que `mes` já tinha).
         *
         * Com `mes` como base, ◀/▶ sempre avançam um mês de calendário a
         * partir de onde o dono REALMENTE está, e nunca ficam presos —
         * inclusive quando isso significa passar batido por um mês vazio.
         * O preço é o cabeçalho poder dizer "AGOSTO 2026" enquanto o cartão
         * mostra os números de julho — mas é exatamente pra isso que serve
         * o aviso logo abaixo ("mostrando julho de 2026"): ele conta a
         * história, o cabeçalho não precisa fingir que é julho.
         */}
        <Cabecalho
          mes={mes}
          onMudarMes={setMes}
          escopo={escopo}
          onMudarEscopo={setEscopo}
          ultimoDia={dadosExibidos.ultimoDia}
          estreito={isNarrowWidth}
        />

        {mesQuaseVazio && (
          <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
            {t('Este mês ainda tem pouco movimento — mostrando {{mes}}', {
              mes: monthUtils.format(mesExibido, "MMMM 'de' yyyy", locale),
            })}
          </Text>
        )}

        {dadosExibidos.erro && (
          <Text style={{ color: cor.saida }}>
            {t('Não consegui ler o mês: {{erro}}', {
              erro: dadosExibidos.erro.message,
            })}
          </Text>
        )}

        {dadosExibidos.carregando && (
          <Text>
            <Trans>Carregando…</Trans>
          </Text>
        )}

        {/*
         * ⚠️ `resumo.naoEhGasto.naoClassificado` é exibido pela Task 9
         * (`TresColunas`, montada logo abaixo do Herói) — não aqui. Ver o
         * JSDoc no topo de `inicio/Heroi.tsx` para o porquê.
         *
         * ⚠️ `resumoExibido` (não `dadosExibidos.resumo`) alimenta os dois
         * primeiros — ver o comentário acima de `resumoExibido`: é o mesmo
         * resumo, só com `fixasPrevistas`/`livre` já descontando as contas
         * fixas ainda não pagas deste mês.
         *
         * ⚠️⚠️ Painel-panorama (Task 9) — a tela Mês fica enxuta: cinco
         * blocos, sem a lista completa de contas fixas (`ContasDoMes.tsx`,
         * agora em `/compromissos`) nem o detalhe por categoria
         * (`OndeFoiODinheiro.tsx`, agora em `/medias`). Os dois arquivos e
         * seus testes continuam existindo — só saem DESTA montagem.
         */}
        {resumoExibido && (
          <>
            <Heroi
              resumo={resumoExibido}
              livreMesAnterior={livreMesAnterior}
              estreito={isNarrowWidth}
            />

            {/*
             * ⚠️⚠️ FIX ROUND 1 (Critical, Task 9) — ver o JSDoc completo em
             * `agendadosErro` (acima, onde `useAgendadosDoMes` é
             * desestruturado). Resumo curto: `fixasPrevistas` vira 0 tanto
             * quando não há conta fixa a pagar quanto quando a consulta de
             * agendados FALHOU (best-effort, `agendados` cai pra `[]`) — e
             * `resumoExibido.livre`, logo acima, JÁ desconta
             * `fixasPrevistas`. Sem este aviso, o número principal da tela
             * fica alto demais, sem nenhum sinal de que faltou dado
             * (`dadosExibidos.erro` não cobre isso: a consulta de
             * `schedules` é independente da de transações).
             */}
            {agendadosErro && (
              <Text
                data-testid="heroi-fixas-incertas"
                style={{ ...texto.corpo, color: cor.tintaFraca }}
              >
                {t(
                  'Não consegui ler seus compromissos — este valor pode estar mais alto que a realidade',
                )}
              </Text>
            )}

            <TresColunas resumo={resumoExibido} estreito={isNarrowWidth} />
            <ResumoCompromissos
              pago={pagoEmFixas}
              aPagar={fixasPrevistas}
              carregando={agendadosCarregando}
              erro={agendadosErro}
            />
            <GastoMedioPorTipo
              medias={mediasDoPeriodo.porTipo}
              esteMes={gastoDoMesPorTipo}
              mesesConsiderados={mediasDoPeriodo.mesesConsiderados}
              estreito={isNarrowWidth}
              carregando={historico.carregando}
              erro={historico.erro}
            />
            <RendaDosMeses
              serie={serie}
              mesDestacado={mesExibido}
              carregando={historico.carregando}
              erro={historico.erro}
            />
          </>
        )}
      </View>
    </Page>
  );
}
